import { z } from "zod";

const MAX_ACCOUNT_OVERRIDES = 100;

/**
 * Shared schema for per-ledger account override entries used by
 * transaction, bill line item, and invoice line item APIs.
 *
 * The override table replaces the legacy `accountId` /
 * `accrualAccountId` / `accountsPayableId` / `accountsReceivableId`
 * columns. An entry pins a specific account id to a specific ledger.
 *
 * Request shape: `accountId` is nullable — passing `null` deletes the
 * override for that ledger. Only ledgers explicitly mentioned in the
 * array are touched; ledgers not listed keep whatever override (if any)
 * they had.
 *
 * Response shape: `accountId` is always non-null — empty / absent
 * overrides are represented by an empty array.
 */
export const accountOverrideEntryRequestSchema = z.object({
    accountingLedgerId: z.string().uuid(),
    accountId: z.string().uuid().nullable(),
});

export const accountOverridesRequestSchema = z
    .array(accountOverrideEntryRequestSchema)
    .max(MAX_ACCOUNT_OVERRIDES);

export const accountOverrideEntryResponseSchema = z.object({
    accountingLedgerId: z.string().uuid(),
    accountId: z.string().uuid(),
});

export const accountOverridesResponseSchema = z.array(
    accountOverrideEntryResponseSchema,
);

export type AccountOverrideEntryRequest = z.infer<
    typeof accountOverrideEntryRequestSchema
>;
export type AccountOverridesRequest = z.infer<
    typeof accountOverridesRequestSchema
>;
export type AccountOverrideEntryResponse = z.infer<
    typeof accountOverrideEntryResponseSchema
>;
export type AccountOverridesResponse = z.infer<
    typeof accountOverridesResponseSchema
>;
